import {
  findDuplicateWines,
  sparkles,
  windowYearsAreCoherent,
  type CataloguedWine,
  type CreateWineRequest,
  type SaveWineRatingRequest,
  type Suggestions,
  type WineType,
} from "@corklog/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { orderCountries } from "../countries.js";
import { decimalOf, formatDecimal, soundDecimal } from "../decimal.js";
import { actionButton, control, fieldLabel, resting, Select } from "./controls.js";
import { addGrape, GrapePicker, Picker } from "./Picker.js";
import { CountrySelect } from "./CountrySelect.js";
import { DuplicateWines } from "./DuplicateWines.js";
import {
  emptyRatingDraft,
  RatingDrafts,
  ratingRequests,
  type RatingDraft,
} from "./RatingDrafts.js";

/**
 * The fields that describe a wine, in one place.
 *
 * Registering and correcting a wine ask for exactly the same things, so they
 * use exactly the same form. Two copies of ten fields drift: a field added to
 * one and forgotten in the other is a wine that can be registered with a
 * dosage and never corrected.
 *
 * The number is not here. It is printed on labels on bottles standing in a
 * cabinet, so it is not a field anybody edits.
 */
const TYPES: readonly WineType[] = [
  "Red",
  "White",
  "Rose",
  "SweetWhite",
  "Dessert",
  "Champagne",
  "Cremant",
  "Sparkling",
];

const CURRENT_YEAR = new Date().getFullYear();

export type WineFormValues = CreateWineRequest;

export const emptyWine: WineFormValues = {
  name: "",
  type: "Red",
  countryCode: "FR",
  producer: "",
  region: null,
  style: null,
  vintage: null,
  grapes: [],
  drinkFrom: null,
  peakFrom: null,
  peakTo: null,
  drinkBy: null,
  isNonVintage: false,
  baseVintage: null,
  disgorgedOn: null,
  dosageGramsPerLitre: null,
  alcoholPercent: null,
};

const asText = (value: number | null | undefined): string =>
  value === null || value === undefined ? "" : String(value);

/**
 * A stored decimal back into the field it was typed into.
 *
 * Not `asText`, which is right for the years and wrong here twice over: it
 * would hand back `12.5` to somebody who typed `12,5`, and `toLocaleString`
 * would hand back `2 019` for a vintage. The two kinds of number are written
 * down differently in Swedish, so they are read back differently here.
 */
const asDecimalText = (value: number | null | undefined): string =>
  value === null || value === undefined ? "" : formatDecimal(value);
const asYear = (value: string): number | null =>
  value.trim() === "" ? null : Number(value);

export function WineForm({
  initial,
  suggestions,
  submitLabel,
  busyLabel,
  onSubmit,
  onCancel,
  offerRatings = false,
  asWish = false,
  catalogue = [],
  cellarId = "",
}: {
  initial: WineFormValues;
  suggestions: Suggestions | null;
  submitLabel: string;
  busyLabel: string;
  onSubmit: (
    values: WineFormValues,
    ratings: SaveWineRatingRequest[],
  ) => Promise<unknown>;
  onCancel?: () => void;
  /**
   * The wines to check this one against, and where to link when one matches.
   *
   * Empty when correcting, which is not an oversight: the wine being corrected
   * is in the catalogue and would match itself, and the offer — *register a
   * lot against the existing number instead* — is an answer to registering,
   * not to editing. The entry already exists by then.
   */
  catalogue?: readonly CataloguedWine[];
  cellarId?: string;
  /**
   * Whether to ask for ratings here.
   *
   * True when registering and false when correcting, and the asymmetry is not
   * an oversight: a wine that exists has its readings on its own page, where
   * each can be corrected and removed on its own date. Showing them here too
   * would be two places to edit one thing, and the one on this form would be
   * the one that could not say *remove the 2019 reading*.
   */
  offerRatings?: boolean;
  /**
   * The form as a wish uses it — `10.64`. Leaves out the alcohol, the grapes
   * and the drinking window: the owner does not want to answer them for a
   * wine that is not in the cellar yet, and a label has not been read for a
   * bottle nobody has. The fields stay in the values, empty, so a wish is
   * still the registration's own request and *Köpt* opens a form that is
   * missing nothing — only those three are asked for there, as always.
   */
  asWish?: boolean;
}) {
  const { t } = useTranslation();

  const [name, setName] = useState(initial.name);
  const [type, setType] = useState<WineType>(initial.type);
  const [countryCode, setCountryCode] = useState(initial.countryCode);
  const countryChoices = orderCountries(
    (suggestions?.countries ?? []).map((row) => row.countryCode),
  );
  const [producer, setProducer] = useState(initial.producer);
  const [region, setRegion] = useState(initial.region ?? "");
  const [style, setStyle] = useState(initial.style ?? "");
  const [vintage, setVintage] = useState(asText(initial.vintage));
  const [grapes, setGrapes] = useState<string[]>([...initial.grapes]);
  /**
   * A grape typed into the field and not added to the list.
   *
   * It lives here rather than inside the picker because saving is this
   * component's decision, and a word standing in a field at the moment of
   * saving is a question — see the alert beside the save button.
   */
  const [grapeDraft, setGrapeDraft] = useState("");
  const [askAboutGrape, setAskAboutGrape] = useState(false);
  const [askAboutDuplicate, setAskAboutDuplicate] = useState(false);
  const [ratingDrafts, setRatingDrafts] = useState<RatingDraft[]>([emptyRatingDraft()]);
  const [isNonVintage, setIsNonVintage] = useState(initial.isNonVintage);
  const [baseVintage, setBaseVintage] = useState(asText(initial.baseVintage));
  const [disgorgedOn, setDisgorgedOn] = useState(initial.disgorgedOn ?? "");
  const [dosage, setDosage] = useState(asDecimalText(initial.dosageGramsPerLitre));
  const [alcohol, setAlcohol] = useState(asDecimalText(initial.alcoholPercent));
  const [drinkFrom, setDrinkFrom] = useState(asText(initial.drinkFrom));
  const [peakFrom, setPeakFrom] = useState(asText(initial.peakFrom));
  const [peakTo, setPeakTo] = useState(asText(initial.peakTo));
  const [drinkBy, setDrinkBy] = useState(asText(initial.drinkBy));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A non-vintage wine has no year, and the flag is what says so. Read once
  // rather than at each use: the duplicate check and the save must agree about
  // what year this wine claims, and two copies of the expression can differ.
  const vintageYear = isNonVintage || vintage.trim() === "" ? null : Number(vintage);

  /**
   * What the cellar already holds of this wine, recomputed as the three fields
   * that decide it are answered — producer, name, year.
   *
   * Over the list the browser already has rather than by asking the server per
   * keystroke; the rule itself is in `shared/`, beside the normaliser the
   * picker matches with. Empty while correcting, and empty on a cellar whose
   * API has not yet learned to send its catalogue.
   */
  const duplicates = findDuplicateWines(
    { name, producer, vintage: vintageYear, isNonVintage },
    catalogue,
  );
  const alreadyCatalogued = duplicates.some((match) => match.kind === "same-vintage");

  const drinkingWindow = {
    drinkFrom: asYear(drinkFrom),
    peakFrom: asYear(peakFrom),
    peakTo: asYear(peakTo),
    drinkBy: asYear(drinkBy),
  };
  const windowIsCoherent = windowYearsAreCoherent(drinkingWindow);
  // The contract's own bounds, checked at the control rather than four seconds
  // later in a 400 — and 25 is the bound that catches `135` typed for `13,5`.
  const alcoholIsSound = soundDecimal(alcohol, 0, 25);
  const dosageIsSound = soundDecimal(dosage, 0, 100);
  const canSubmit =
    name.trim() !== "" &&
    producer.trim() !== "" &&
    windowIsCoherent &&
    alcoholIsSound &&
    dosageIsSound &&
    !busy;

  // The same set of types the walk through the cellar asks for back labels
  // from, named once in `shared/` rather than twice with a chance of drifting.
  const isSparkling = sparkles(type);

  const pendingGrape = grapeDraft.trim();

  const save = (grapesToSave: readonly string[]) => {
    setBusy(true);
    setError(null);
    void onSubmit(
      {
        name,
        type,
        countryCode,
        producer,
        region: region.trim() === "" ? null : region,
        style: style.trim() === "" ? null : style,
        vintage: vintageYear,
        grapes: [...grapesToSave],
        isNonVintage,
        baseVintage:
          isSparkling && baseVintage.trim() !== "" ? Number(baseVintage) : null,
        disgorgedOn: isSparkling && disgorgedOn !== "" ? disgorgedOn : null,
        // Through the comma rule, like everything decimal that a Swedish
        // keyboard types: `Number("9,5")` is `NaN`, and until this was read
        // here it reached the server as one.
        dosageGramsPerLitre:
          isSparkling && dosage.trim() !== "" ? decimalOf(dosage) : null,
        alcoholPercent: alcohol.trim() === "" ? null : decimalOf(alcohol),
        ...drinkingWindow,
      },
      offerRatings ? ratingRequests(ratingDrafts, suggestions) : [],
    )
      .catch(() => setError(t("wines.saveFailed")))
      .finally(() => setBusy(false));
  };

  /**
   * Saving, and the two things that stop it first.
   *
   * **The duplicate comes before the grape**, because it is the question about
   * whether to save at all and the other is about what to save. Answering it
   * *save anyway* passes the answer along rather than storing it, so it holds
   * for that press and no other — the fields can change underneath a stored
   * acknowledgement, and then it would be an answer to a question nobody
   * asked.
   */
  const attemptSave = (duplicateAcknowledged: boolean) => {
    // A number is allocated once and never reused, so a second entry for a
    // wine already catalogued cannot be taken back — only merged, by a tool
    // that does not exist yet. That is why this is asked at the button and not
    // only in the notice further up the form, which can be scrolled past.
    if (alreadyCatalogued && !duplicateAcknowledged) {
      setAskAboutDuplicate(true);
      return;
    }
    // A grape typed and not added is the one thing on this form that would
    // be thrown away without a word. Saving stops and asks instead — once,
    // and never as a browser dialog, which is a thing to dismiss rather
    // than a thing to read.
    if (pendingGrape !== "") {
      setAskAboutGrape(true);
      return;
    }
    save(grapes);
  };

  return (
    <form
      className="grid gap-4 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) return;
        attemptSave(false);
      }}
    >
      <Field id="wine-name" label={t("wines.name")}>
        <input
          id="wine-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          className={`${control} ${resting}`}
        />
      </Field>

      <Picker
        id="wine-producer"
        label={t("wines.producer")}
        value={producer}
        onChange={setProducer}
        options={(suggestions?.producers ?? []).map((row) => row.name)}
      />

      <Field id="wine-type" label={t("wines.type")}>
        <Select
          id="wine-type"
          value={type}
          onChange={(next) => setType(next as WineType)}
        >
          {TYPES.map((value) => (
            <option key={value} value={value}>
              {t(`wineType.${value}`)}
            </option>
          ))}
        </Select>
      </Field>

      <Field id="wine-country" label={t("wines.country")}>
        {/* The countries this cellar already buys from, first, then every
            other country there is — both in Swedish alphabetical order since
            2026-09-29, with a line between (`orderCountries`). An answer is
            required here, so there is no "any" row and `value` is never null.

            The tail was eight hard-coded codes until `9.10`, which meant a
            wine from anywhere else could not be registered at all — the same
            shape of mistake the purchase source made in the schema. The
            cellar's own countries staying on top is what keeps a list of 249
            usable: in practice the answer is in the first four rows. */}
        <CountrySelect
          id="wine-country"
          value={countryCode}
          onChange={(next) => setCountryCode(next ?? countryCode)}
          {...countryChoices}
        />
      </Field>

      <Picker
        id="wine-region"
        label={t("wines.region")}
        value={region}
        onChange={setRegion}
        options={(suggestions?.regions ?? [])
          .filter((row) => row.countryCode === countryCode)
          .map((row) => row.name)}
      />

      {/* Beside the region rather than beside the type: Amarone and Barolo are
          answers to "where is this from and what is it", and the cellar's own
          list is what stops a twenty-fifth style appearing that is really the
          third. Left empty for as long as the owner has not decided. */}
      <Picker
        id="wine-style"
        label={t("wines.style")}
        value={style}
        onChange={setStyle}
        options={(suggestions?.styles ?? []).map((row) => row.name)}
      />

      {/* Not among the Champagne fields below, because it is not a Champagne
          fact: every bottle carries it, and it is read off the same back label
          as the dosage only because that is where small print lives. Before
          the vintage rather than after, so that the year stays the last of the
          three answers the duplicate notice waits for. */}
      {!asWish && (
        <Field id="wine-alcohol" label={t("wines.alcohol")}>
          <input
            id="wine-alcohol"
            inputMode="decimal"
            value={alcohol}
            aria-invalid={!alcoholIsSound}
            onChange={(event) => setAlcohol(event.target.value)}
            placeholder="13,5"
            className={`${control} ${resting}`}
          />
          {/* Said at the field and not at the button, because the number that is
            wrong is here. `135` for `13,5` is the mistake a decimal field
            invites, and it is the one the bound is drawn to catch. */}
          {!alcoholIsSound && (
            <p role="alert" className="text-past mt-1 text-sm">
              {t("wines.alcoholOutOfRange")}
            </p>
          )}
        </Field>
      )}

      <Field id="wine-vintage" label={t("wines.vintage")}>
        <input
          id="wine-vintage"
          inputMode="numeric"
          value={isNonVintage ? "" : vintage}
          disabled={isNonVintage}
          onChange={(event) => setVintage(event.target.value)}
          placeholder={isNonVintage ? t("wines.nvShort") : String(CURRENT_YEAR)}
          className={`${control} ${resting} disabled:opacity-40`}
        />
        <label className="mt-2 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isNonVintage}
            onChange={(event) => setIsNonVintage(event.target.checked)}
            className="accent-accent"
          />
          {t("wines.nonVintage")}
        </label>
      </Field>

      {/* Here rather than at the top of the form: the producer, the name and
          the year are the three answers that decide it, and the year is the
          last of them — so this is where the eye is at the moment the question
          can first be answered. It informs and links; the question about what
          to do is asked at the button, where saving happens. */}
      <DuplicateWines matches={duplicates} cellarId={cellarId} />

      {!asWish && (
        <div className="sm:col-span-2">
          <GrapePicker
            values={grapes}
            onChange={setGrapes}
            options={(suggestions?.grapes ?? []).map((row) => row.name)}
            draft={grapeDraft}
            onDraftChange={(next) => {
              setGrapeDraft(next);
              // Typing again is an answer of its own: the question was about the
              // word that stood there, and that word has changed.
              setAskAboutGrape(false);
            }}
          />
        </div>
      )}

      {isSparkling && (
        <fieldset className="border-line col-span-full mt-6 border-t pt-5">
          <legend className="font-display text-ink px-2 text-base">
            {t("wines.bubbles")}
          </legend>
          <p className="text-muted mb-3 max-w-prose text-sm">{t("wines.bubblesHint")}</p>
          <div className="grid gap-4 sm:grid-cols-3">
            <YearField
              id="w-base"
              label={t("wines.baseVintage")}
              value={baseVintage}
              onChange={setBaseVintage}
            />
            <div>
              <label className={fieldLabel} htmlFor="w-disgorged">
                {t("wines.disgorgedOn")}
              </label>
              <input
                id="w-disgorged"
                type="date"
                value={disgorgedOn}
                onChange={(event) => setDisgorgedOn(event.target.value)}
                className={`${control} ${resting} mt-1`}
              />
            </div>
            <div>
              <label className={fieldLabel} htmlFor="w-dosage">
                {t("wines.dosage")}
              </label>
              <input
                id="w-dosage"
                inputMode="decimal"
                value={dosage}
                aria-invalid={!dosageIsSound}
                onChange={(event) => setDosage(event.target.value)}
                placeholder="6"
                className={`${control} ${resting} mt-1`}
              />
              {/* The walk through the cabinets has said this at the field
                  since `8.2`; this form disabled its own save without a word
                  until the alcohol field arrived and made the silence
                  obvious. */}
              {!dosageIsSound && (
                <p role="alert" className="text-past mt-1 text-sm">
                  {t("wines.dosageOutOfRange")}
                </p>
              )}
            </div>
          </div>
        </fieldset>
      )}

      {/* Beside the wine and not on a page of its own, because the Vivino
          average and the Munskänkarna score are in front of you at the moment
          the bottle is being catalogued, and a field asked for on a second
          screen is a field filled in later — which in practice means never. */}
      {offerRatings && (
        <fieldset className="border-line col-span-full mt-6 border-t pt-5">
          <legend className="font-display text-ink px-2 text-base">
            {t("wine.ratings.heading")}
          </legend>
          <p className="text-muted mb-3 max-w-prose text-sm">
            {t("wine.ratings.registerHint")}
          </p>
          <RatingDrafts
            drafts={ratingDrafts}
            onChange={setRatingDrafts}
            suggestions={suggestions}
          />
        </fieldset>
      )}

      {!asWish && (
        <fieldset className="border-line col-span-full mt-6 border-t pt-5">
          <legend className="font-display text-ink px-2 text-base">
            {t("wines.window")}
          </legend>
          <p className="text-muted mb-3 max-w-prose text-sm">{t("wines.windowHint")}</p>
          <div className="grid gap-4 sm:grid-cols-4">
            <YearField
              id="w-from"
              label={t("wines.drinkFrom")}
              value={drinkFrom}
              onChange={setDrinkFrom}
            />
            <YearField
              id="w-peak-from"
              label={t("wines.peakFrom")}
              value={peakFrom}
              onChange={setPeakFrom}
            />
            <YearField
              id="w-peak-to"
              label={t("wines.peakTo")}
              value={peakTo}
              onChange={setPeakTo}
            />
            <YearField
              id="w-by"
              label={t("wines.drinkBy")}
              value={drinkBy}
              onChange={setDrinkBy}
            />
          </div>
          {!windowIsCoherent && (
            <p role="alert" className="text-past mt-3 text-sm">
              {t("wines.windowBackwards")}
            </p>
          )}
        </fieldset>
      )}

      {/* The same two-answer shape as the grape question below, and asked in
          the same place for the same reason. The first answer leaves the form:
          a lot is registered on the wine's own page, which is where its
          bottles live. The second is a word, because it is the rarer of the
          two and because it allocates a number that cannot be taken back —
          but it is offered plainly, since two genuinely different wines can
          share a producer, a name and a year, and only the person holding the
          bottle knows. */}
      {askAboutDuplicate && alreadyCatalogued && (
        <div role="alert" className="border-accent col-span-full rounded-sm border p-4">
          <p className="text-sm">{t("wines.duplicate.ask")}</p>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <Link
              to={`/kallare/${cellarId}/viner/${duplicates[0]?.wine.id ?? ""}`}
              className={actionButton}
            >
              {t("wines.duplicate.openWine")}
            </Link>
            <button
              type="button"
              className="text-muted hover:text-ink text-sm underline-offset-4 hover:underline"
              onClick={() => {
                setAskAboutDuplicate(false);
                attemptSave(true);
              }}
            >
              {t("wines.duplicate.saveAnyway")}
            </button>
          </div>
        </div>
      )}

      {/* The question, where the saving happens rather than up beside the field
          it is about: it is raised by pressing save, and it is answered by
          pressing something else in the same place. Both answers are offered
          plainly — keeping the grape is the likelier intention and is shaped
          like a button, discarding it is a word — because a question whose
          second answer is hidden is not a question. */}
      {askAboutGrape && pendingGrape !== "" && (
        <div role="alert" className="border-accent col-span-full rounded-sm border p-4">
          <p className="text-sm">{t("wines.grapeNotAdded", { name: pendingGrape })}</p>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <button
              type="button"
              className={actionButton}
              onClick={() => {
                const next = addGrape(grapes, pendingGrape);
                setGrapes(next);
                setGrapeDraft("");
                setAskAboutGrape(false);
                save(next);
              }}
            >
              {t("wines.addGrapeAndSave")}
            </button>
            <button
              type="button"
              className="text-muted hover:text-ink text-sm underline-offset-4 hover:underline"
              onClick={() => {
                setGrapeDraft("");
                setAskAboutGrape(false);
                save(grapes);
              }}
            >
              {t("wines.saveWithoutGrape")}
            </button>
          </div>
        </div>
      )}

      <div className="border-line col-span-full mt-2 flex items-center gap-4 border-t pt-5">
        <button type="submit" disabled={!canSubmit} className={actionButton}>
          {busy ? busyLabel : submitLabel}
        </button>
        {onCancel !== undefined && (
          <button
            type="button"
            onClick={onCancel}
            className="text-muted hover:text-ink text-sm underline-offset-4 hover:underline"
          >
            {t("wines.cancel")}
          </button>
        )}
        {error !== null && (
          <span role="alert" className="text-past text-sm">
            {error}
          </span>
        )}
      </div>
    </form>
  );
}

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className={fieldLabel} htmlFor={id}>
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function YearField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className={fieldLabel} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        inputMode="numeric"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`${control} ${resting} mt-1`}
      />
    </div>
  );
}
